/**
 * Copyright 2025 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */

export type VehicleParams = {
  year?: number | string;
  makeId?: number | string;
  modelId?: number | string;
  vehicleTypeId?: number | string;
  vehicleQuestions?: string;
  subModelId?: number | string;
  driveTypeId?: number | string;
  engineId?: number | string;
  [key: string]: number | string | undefined; // Allow additional vehicle parameters
};

export type ValidatedVehicleParams = {
  year?: number;
  makeId?: number;
  modelId?: number;
  vehicleTypeId?: number;
  vehicleQuestions?: string;
  subModelId?: number;
  driveTypeId?: number;
  engineId?: number;
  [key: string]: number | string | undefined;
};

/**
 * Validates and filters vehicle parameters.
 *
 * Business Rules:
 * 1. ALL base parameters (year, makeId, modelId) must be present
 * 2. makeId and modelId must be positive numbers (greater than 0)
 * 3. If ANY base parameter is missing/invalid, return empty object
 * 4. If ALL base parameters are valid, return all vehicle parameters
 *
 * @param params - Raw vehicle parameters from preferredVehicle
 * @returns Validated parameters or empty object
 */
export function validateAndFilterVehicleParams(
  params: VehicleParams | undefined | null,
): ValidatedVehicleParams {
  // Return empty object if no params provided
  if (!params) {
    return {};
  }

  // Convert and validate year
  const year = params.year !== undefined && params.year !== null ? Number(params.year) : NaN;

  // Convert and validate makeId
  const makeId =
    params.makeId !== undefined && params.makeId !== null ? Number(params.makeId) : NaN;

  // Convert and validate modelId
  const modelId =
    params.modelId !== undefined && params.modelId !== null ? Number(params.modelId) : NaN;

  // Check base parameter validation rules:
  // 1. Year must exist and be a valid number
  // 2. MakeId must exist, be a valid number, and be greater than 0
  // 3. ModelId must exist, be a valid number, and be greater than 0
  const isYearValid = !isNaN(year);
  const isMakeIdValid = !isNaN(makeId) && makeId > 0;
  const isModelIdValid = !isNaN(modelId) && modelId > 0;

  // If ANY base parameter is invalid, return empty object
  if (!isYearValid || !isMakeIdValid || !isModelIdValid) {
    return {};
  }

  // All base parameters are valid, build the validated params object
  const validatedParams: ValidatedVehicleParams = {
    year,
    makeId,
    modelId,
  };

  // Add optional vehicle parameters if they exist
  // vehicleTypeId
  if (params.vehicleTypeId !== undefined && params.vehicleTypeId !== null) {
    const vehicleTypeId = Number(params.vehicleTypeId);
    if (!isNaN(vehicleTypeId) && vehicleTypeId !== 0) {
      validatedParams.vehicleTypeId = vehicleTypeId;
    }
  }

  // vehicleQuestions (string, no conversion needed)
  if (params.vehicleQuestions) {
    validatedParams.vehicleQuestions = params.vehicleQuestions;
  }

  // subModelId
  if (params.subModelId !== undefined && params.subModelId !== null) {
    const subModelId = Number(params.subModelId);
    if (!isNaN(subModelId) && subModelId !== 0) {
      validatedParams.subModelId = subModelId;
    }
  }

  // driveTypeId
  if (params.driveTypeId !== undefined && params.driveTypeId !== null) {
    const driveTypeId = Number(params.driveTypeId);
    if (!isNaN(driveTypeId) && driveTypeId !== 0) {
      validatedParams.driveTypeId = driveTypeId;
    }
  }

  // engineId
  if (params.engineId !== undefined && params.engineId !== null) {
    const engineId = Number(params.engineId);
    if (!isNaN(engineId) && engineId !== 0) {
      validatedParams.engineId = engineId;
    }
  }

  // Copy any other vehicle-related parameters that might exist
  // This ensures we don't miss any custom vehicle parameters
  Object.keys(params).forEach((key) => {
    // Skip already processed keys
    if (
      ![
        'year',
        'makeId',
        'modelId',
        'vehicleTypeId',
        'vehicleQuestions',
        'subModelId',
        'driveTypeId',
        'engineId',
      ].includes(key)
    ) {
      const value = params[key];
      if (value !== undefined && value !== null) {
        validatedParams[key] = value;
      }
    }
  });

  return validatedParams;
}

/**
 * Helper function to check if vehicle parameters are complete.
 * Useful for logging or conditional logic.
 *
 * @param params - Vehicle parameters to check
 * @returns true if all base parameters are valid, false otherwise
 */
export function hasCompleteVehicleParams(params: VehicleParams | undefined | null): boolean {
  if (!params) {
    return false;
  }

  const year = params.year !== undefined && params.year !== null ? Number(params.year) : NaN;

  const makeId =
    params.makeId !== undefined && params.makeId !== null ? Number(params.makeId) : NaN;

  const modelId =
    params.modelId !== undefined && params.modelId !== null ? Number(params.modelId) : NaN;

  return !isNaN(year) && !isNaN(makeId) && makeId > 0 && !isNaN(modelId) && modelId > 0;
}
